import React, { useCallback, useMemo, useRef, useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { View, Dimensions, Platform, Text } from 'react-native';
import { useSelector } from 'react-redux';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { ReactNativeZoomableView } from '@openspacelabs/react-native-zoomable-view';
import DraggableDepartment, { DraggableDepartmentHandle } from '@components/Hierarchy/DraggableDepartment';
import { Svg, Defs, Pattern, Rect, Line } from 'react-native-svg';
import HierarchyArrowRenderer, { HierarchyArrowRendererHandle } from './HierarchyArrowRenderer';
import { debounce } from 'lodash';

// Arrow data interface (matching DraggableUserCard)
export interface ArrowData {
  id: string;
  path: string;
  color: string;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  angle: number;
  reset?: boolean;
}


// Enhanced User interfaces
interface UserProfile {
  name?: string;
  surname?: string;
  avatar?: any;
  department?: string;
}

interface User {
  _id: string;
  email?: string;
  profile_information?: UserProfile;
  department?: string;
  role?: string;
  status?: string;
  user?: {
    _id: string;
    department?: string;
    profile_information?: {
      department?: string;
    };
  };
}

interface Department {
  name: string;
  users: User[];
  color: string;
  count: number;
}

interface DepartmentHierarchyProps {
  userList: User[];
  owner?: User;
  isLoading?: boolean;
  onDepartmentPositionChange?: (departmentName: string, position: { x: number; y: number }) => void;
  onDepartmentSnap?: (targetDepartment: string, sourceDepartment: string) => void; // Callback when arrow snaps
}

export interface DepartmentHierarchyHandle {
  centerAllDepartments: () => void;
  zoomTo: (zoom: number, center: { x: number; y: number }) => void;
  zoomBy: (delta: number) => void;
  getCurrentZoom: () => number;
  getAllDepartmentPositions: () => Record<string, { x: number; y: number }>;
}

// Constants
const VIEWPORT_SIZE = { width: 4000, height: 4000 };
const GRID_SIZE = 50;

// Department color mapping
const getDepartmentColor = (departmentName: string) => {
  const colors: Record<string, string> = {
    'Executive': '#8b5cf6',
    'Engineering': '#06b6d4',
    'Sales': '#10b981',
    'Marketing': '#f59e0b',
    'Human Resources': '#ef4444',
    'Reception': '#6B7280',
    'General': '#8B5CF6',
    'Unassigned': '#9CA3AF'
  };
  return colors[departmentName] || '#6b7280';
};

// Department grouping utility
const groupUsersByDepartment = (users: User[]) => {
  const grouped = users.reduce((acc, user) => {
    // Handle both nested user structure (from API) and direct user objects
    // API returns: { user: { profile_information: { department: 'X' } } }
    // Direct user: { department: 'X' } or { profile_information: { department: 'X' } }
    let dept = 'Unassigned';

    if (user.user) {
      // Nested structure from API (company.users or user.connectedUsers)
      // Priority: company.users.department > user.profile_information.department > user.department
      dept = user.department || // ← Company-level department (highest priority)
        user.user.profile_information?.department ||
        user.user.department ||
        'Unassigned';
    } else {
      // Direct user object
      dept = user.department || // ← Company-level department (highest priority)
        user.profile_information?.department ||
        'Unassigned';
    }

    // Ensure users without any department are marked as "Unassigned"
    if (!dept || dept === '' || dept === null || dept === undefined) {
      dept = 'Unassigned';
    }

    if (!acc[dept]) {
      acc[dept] = [];
    }

    // Push the actual user object (unwrap if nested)
    acc[dept].push(user.user || user);
    return acc;
  }, {} as Record<string, User[]>);

  // Sort departments by priority (Executive first, then alphabetically, Unassigned last)
  const priority = ['Executive', 'Engineering', 'Sales', 'Marketing', 'Human Resources', 'Reception'];
  const sortedDepartments = Object.keys(grouped).sort((a, b) => {
    // Unassigned always goes last
    if (a === 'Unassigned') return 1;
    if (b === 'Unassigned') return -1;

    const aIndex = priority.indexOf(a);
    const bIndex = priority.indexOf(b);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.localeCompare(b);
  });

  return sortedDepartments.map(dept => ({
    name: dept,
    users: grouped[dept],
    color: getDepartmentColor(dept),
    count: grouped[dept].length
  }));
};

// Grid Background
const GridBackground = ({ theme, zoomLevel }: { theme: boolean; zoomLevel: number }) => {
  const gridOpacity = Math.max(0.3, Math.min(0.8, zoomLevel * 0.5 + 0.3));
  const gridColor = theme ? '#cccccc' : '#444444';

  return (
    <Svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} width="100%" height="100%">
      <Defs>
        <Pattern id="grid" width={GRID_SIZE} height={GRID_SIZE} patternUnits="userSpaceOnUse">
          <Rect width={GRID_SIZE} height={GRID_SIZE} fill="transparent" />
          <Line x1={0} y1={0} x2={0} y2={GRID_SIZE} stroke={gridColor} strokeWidth={1} opacity={gridOpacity} />
          <Line x1={0} y1={0} x2={GRID_SIZE} y2={0} stroke={gridColor} strokeWidth={1} opacity={gridOpacity} />
        </Pattern>
      </Defs>
      <Rect width="100%" height="100%" fill="url(#grid)" />
    </Svg>
  );
};

const DepartmentHierarchy = forwardRef<DepartmentHierarchyHandle, DepartmentHierarchyProps>(
  ({ userList, owner, isLoading = false, onDepartmentPositionChange, onDepartmentSnap }, ref) => {
    const theme = themeSelector();

    // Use ref for zoomLevel to prevent re-renders on zoom events
    const zoomLevelRef = useRef(0.6);
    const [gridZoomLevel, setGridZoomLevel] = useState(0.6); // Only for GridBackground if needed

    const zoomableViewRef = useRef<ReactNativeZoomableView>(null);
    const departmentRefs = useRef<Map<string, DraggableDepartmentHandle>>(new Map());
    const departmentPositionsRef = useRef<Record<string, { x: number; y: number }>>({});

    // Arrow state management - FULLY ref-based (NO parent state)
    const arrowsRef = useRef<Map<string, ArrowData>>(new Map());
    const arrowRendererRef = useRef<HierarchyArrowRendererHandle>(null);

    // Arrow handler - receives arrow data from any DraggableDepartment
    // CRITICAL: Fully ref-based - NO state updates, NO parent re-renders
    const handleArrowUpdate = useCallback((arrowData: ArrowData) => {
      // Validate coordinates before storing
      const isValidNumber = (n: number) => typeof n === 'number' && !isNaN(n) && isFinite(n);

      if (!arrowData.reset && (!isValidNumber(arrowData.startX) || !isValidNumber(arrowData.startY) ||
        !isValidNumber(arrowData.endX) || !isValidNumber(arrowData.endY))) {
        console.warn('⚠️ Invalid arrow coordinates, skipping:', arrowData.id, {
          startX: arrowData.startX,
          startY: arrowData.startY,
          endX: arrowData.endX,
          endY: arrowData.endY
        });
        return;
      }

      // Update ref immediately (NO parent state update!)
      if (arrowData.reset) {
        arrowsRef.current.delete(arrowData.id);
      } else {
        arrowsRef.current.set(arrowData.id, arrowData);
      }

      // Trigger arrow renderer to update via imperative handle
      // This updates state INSIDE arrow renderer, NOT in parent
      if (arrowRendererRef.current) {
        arrowRendererRef.current.forceUpdate();
      }
    }, []); // EMPTY DEPS - function uses only refs

    // Group users by department - INCLUDE OWNER!
    const departmentGroups = useMemo(() => {
      // Combine owner with userList to ensure owner is always shown
      const allUsers = owner ? [owner, ...userList] : userList;

      console.log('🔍 DepartmentHierarchy - Raw data before grouping:', {
        hasOwner: !!owner,
        owner: owner,
        userList: userList,
        userListIsArray: Array.isArray(userList),
        ownerData: owner ? {
          _id: owner._id,
          email: owner.email,
          profile_information: owner.profile_information,
          department: owner.department,
          profileDepartment: owner.profile_information?.department
        } : null,
        userListLength: userList?.length,
        allUsersLength: allUsers?.length,
        userListSample: userList?.slice(0, 2).map(u => ({
          _id: u?._id,
          email: u?.email,
          department: u?.department,
          profileDepartment: u?.profile_information?.department,
          fullUser: u
        }))
      });

      const groups = groupUsersByDepartment(allUsers);
      console.log('🔍 DepartmentHierarchy grouping result:', {
        hasOwner: !!owner,
        ownerDepartment: owner?.profile_information?.department || owner?.department,
        userListLength: userList?.length,
        allUsersLength: allUsers.length,
        groupsLength: groups.length,
        groups: groups.map(g => ({
          name: g.name,
          usersCount: g.users?.length,
          users: g.users?.map(u => ({
            _id: u._id,
            name: u.profile_information?.name || u.name,
            department: u.department || u.profile_information?.department
          }))
        })),
        departmentNames: groups.map(g => g.name),
        hasUnassigned: groups.some(g => g.name === 'Unassigned')
      });
      return groups;
    }, [userList, owner]);

    // Calculate initial positions for departments in vertical layout starting with Executive
    const calculateInitialPositions = useCallback(() => {
      const positions: Record<string, { x: number; y: number }> = {};
      const centerX = VIEWPORT_SIZE.width / 2;
      const startY = 500; // Start from top area of viewport

      // Department card dimensions
      const cardWidth = 280;
      const cardHeight = 400;
      const verticalSpacing = cardHeight + 150; // Increased spacing between departments (400 + 150 = 550px total)

      departmentGroups.forEach((department, index) => {
        // Center horizontally, distribute vertically with adequate spacing
        positions[department.name] = {
          x: centerX - (cardWidth / 2), // Center the department card horizontally
          y: startY + (index * verticalSpacing), // Stack vertically with generous spacing
        };
      });

      console.log('🎯 Calculated vertical department positions with adequate spacing:', positions);
      console.log('🎯 Position details:', {
        totalDepartments: departmentGroups.length,
        centerX,
        startY,
        verticalSpacing,
        positions: Object.entries(positions).map(([name, pos]) => ({
          name,
          x: pos.x,
          y: pos.y,
          index: departmentGroups.findIndex(d => d.name === name)
        }))
      });
      return positions;
    }, [departmentGroups]);

    // Initialize positions and center view
    useEffect(() => {
      const initialPositions = calculateInitialPositions();
      departmentPositionsRef.current = initialPositions;
      console.log('🎯 Initialized department positions:', initialPositions);

      // Center the view on departments after a short delay
      const timer = setTimeout(() => {
        if (zoomableViewRef.current) {
          const centerX = VIEWPORT_SIZE.width / 2;
          // Calculate center based on number of departments and spacing
          const totalHeight = departmentGroups.length * 550; // 400 (card) + 150 (spacing)
          const centerY = 500 + (totalHeight / 2); // Start position + half of total height
          const screenWidth = Dimensions.get('window').width;
          const screenHeight = Dimensions.get('window').height;

          const offsetX = -(centerX - screenWidth / 2);
          const offsetY = -(centerY - screenHeight / 2);

          zoomableViewRef.current.moveTo(offsetX, offsetY);
          zoomableViewRef.current.zoomTo(0.5); // Slightly reduced zoom to show more departments
        }
      }, 100);

      return () => clearTimeout(timer);
    }, [calculateInitialPositions]);

    // Handle department position changes - STABLE REFERENCE
    const handleDepartmentPositionChange = useCallback((departmentName: string, newPosition: { x: number; y: number }) => {
      departmentPositionsRef.current[departmentName] = newPosition;

      if (onDepartmentPositionChange) {
        onDepartmentPositionChange(departmentName, newPosition);
      }

      // console.log(`🎯 Department ${departmentName} moved to:`, newPosition);
    }, []); // EMPTY DEPS - uses refs only

    // Handle continuous position updates during drag - STABLE REFERENCE
    const handleDepartmentPositionUpdate = useCallback((departmentName: string, newPosition: { x: number; y: number }) => {
      // Update ref for real-time tracking (no state update to avoid re-renders)
      departmentPositionsRef.current[departmentName] = newPosition;
    }, []); // EMPTY DEPS - uses refs only

    // Stable snap handler - calls parent callback when arrow snaps
    const handleDepartmentSnap = useCallback((targetId: string, sourceId: string) => {
      console.log('🎯🎯🎯 Department snap detected in handler:', {
        sourceId,
        targetId,
        hasParentCallback: !!onDepartmentSnap
      });

      // Call parent callback (e.g., show link modal, create connection)
      if (onDepartmentSnap) {
        console.log('✅ Calling parent onDepartmentSnap callback');
        onDepartmentSnap(targetId, sourceId);
      } else {
        console.warn('⚠️ No onDepartmentSnap callback provided from parent');
      }
    }, []); // EMPTY DEPS - uses closure over onDepartmentSnap

    // Stable position getter
    const getAllDepartmentPositions = useCallback(() => {
      return departmentPositionsRef.current;
    }, []); // EMPTY DEPS - returns ref

    // Center all departments in vertical layout
    const centerAllDepartments = useCallback(() => {
      const initialPositions = calculateInitialPositions();
      departmentPositionsRef.current = initialPositions;

      // Update each department's position using refs
      Object.entries(initialPositions).forEach(([departmentName, position]) => {
        const departmentRef = departmentRefs.current.get(departmentName);
        if (departmentRef) {
          departmentRef.setPosition(position);
        }
      });

      // Center the view on the departments
      if (zoomableViewRef.current) {
        const centerX = VIEWPORT_SIZE.width / 2;
        // Calculate center based on number of departments and spacing
        const totalHeight = departmentGroups.length * 550; // 400 (card) + 150 (spacing)
        const centerY = 500 + (totalHeight / 2); // Start position + half of total height
        const screenWidth = Dimensions.get('window').width;
        const screenHeight = Dimensions.get('window').height;

        const offsetX = -(centerX - screenWidth / 2);
        const offsetY = -(centerY - screenHeight / 2);

        console.log('🎯 Viewport centering:', {
          totalHeight,
          centerY,
          screenWidth,
          screenHeight,
          offsetX,
          offsetY,
          zoom: 0.5
        });

        zoomableViewRef.current.moveTo(offsetX, offsetY);
        zoomableViewRef.current.zoomTo(0.5); // Slightly reduced zoom to show more departments
      }

      console.log('🎯 Centered all departments in vertical layout');
    }, [calculateInitialPositions]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      centerAllDepartments,
      zoomTo: (zoom: number, center: { x: number; y: number }) => {
        zoomableViewRef.current?.zoomTo(zoom, center);
      },
      zoomBy: (delta: number) => {
        zoomableViewRef.current?.zoomBy(delta);
      },
      getCurrentZoom: () => zoomLevelRef.current,
      getAllDepartmentPositions: () => departmentPositionsRef.current,
    }));

    // Only log on initial render, not every re-render
    const renderCountRef = useRef(0);
    useEffect(() => {
      renderCountRef.current += 1;
      if (renderCountRef.current > 2) {
        console.warn('⚠️ DepartmentHierarchy re-rendering!', renderCountRef.current);
      } else if (renderCountRef.current === 1) {
        console.log('🔍 DepartmentHierarchy initial render:', {
          propsReceived: {
            hasOwner: !!owner,
            ownerEmail: owner?.email,
            userListLength: userList?.length,
            isLoading
          },
          computed: {
            departmentGroupsLength: departmentGroups.length,
          }
        });
      }
    });

    if (isLoading) {
      return (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Text style={{ color: theme ? '#2d3748' : '#ffffff' }}>Loading departments...</Text>
        </View>
      );
    }

    return (
      <View style={{ flex: 1 }}>
        <ReactNativeZoomableView
          ref={zoomableViewRef}
          maxZoom={3}
          minZoom={0.2}
          initialZoom={0.5}
          contentWidth={VIEWPORT_SIZE.width}
          contentHeight={VIEWPORT_SIZE.height}
          bindToBorders={true}
          panBoundaryPadding={200}
          style={{ flex: 1 }}
          onZoomAfter={(event, gestureState, zoomableViewEventObject) => {
            // Update ref only - no state update to prevent re-renders
            zoomLevelRef.current = zoomableViewEventObject.zoomLevel;

            // Only update grid zoom if on web (for GridBackground component)
            if (Platform.OS === 'web') {
              setGridZoomLevel(zoomableViewEventObject.zoomLevel);
            }
          }}
        >
          <View style={{ width: VIEWPORT_SIZE.width, height: VIEWPORT_SIZE.height }}>
            {/* Grid Background */}
            {Platform.OS === 'web' && <GridBackground theme={theme} zoomLevel={gridZoomLevel} />}

            {/* Render Department Cards */}
            {departmentGroups.map((department) => {
              const position = departmentPositionsRef.current[department.name] || { x: 1000, y: 1000 };

              // Only log first department to reduce spam
              // if (department.name === departmentGroups[0]?.name) {
              //   console.log('🎯 Rendering first department:', department.name, position);
              // }

              return (
                <DraggableDepartment
                  key={department.name}
                  ref={(ref) => {
                    if (ref) {
                      departmentRefs.current.set(department.name, ref);
                    } else {
                      departmentRefs.current.delete(department.name);
                    }
                  }}
                  department={department}
                  initialPosition={position}
                  onPositionChange={handleDepartmentPositionChange}
                  onPositionUpdate={handleDepartmentPositionUpdate}
                  dragEnabled={true}
                  zoomLevel={zoomLevelRef.current}
                  theme={theme}
                  showArrows={true}
                  onSnap={handleDepartmentSnap}
                  getAllDepartmentPositions={getAllDepartmentPositions}
                  arrowHandler={handleArrowUpdate}
                />
              );
            })}

            {/* Centralized Arrow Renderer - ISOLATED to prevent parent re-renders */}
            {/* State is managed INSIDE renderer via imperative handle */}
            {/* Parent has ZERO state updates related to arrows */}
            <HierarchyArrowRenderer
              ref={arrowRendererRef}
              arrowsRef={arrowsRef}
              width={VIEWPORT_SIZE.width}
              height={VIEWPORT_SIZE.height}
              zoomLevel={zoomLevelRef.current}
              zIndex={100}
            />
          </View>
        </ReactNativeZoomableView>
      </View>
    );
  }
);

DepartmentHierarchy.displayName = 'DepartmentHierarchy';

export default DepartmentHierarchy;
